
/**
 * Centralized Feature Configuration - Frontend
 * 
 * This mirrors the backend feature configuration and adds UI-specific metadata.
 */

export const FEATURE_CATEGORIES = {
  INVENTORY: 'inventory',
  USERS: 'users',
  DASHBOARD: 'dashboard',
  SETTINGS: 'settings'
}

export const FEATURES = {
  // Inventory Category
  INVENTORY: {
    key: 'inventory',
    name: 'Inventory Management',
    category: FEATURE_CATEGORIES.INVENTORY,
    description: 'School inventory and stock management',
    defaultEnabled: true,
    icon: '📦'
  },
  ASSET_MANAGEMENT: {
    key: 'assetManagement',
    name: 'Asset Management',
    category: FEATURE_CATEGORIES.INVENTORY,
    description: 'Track and manage school assets',
    defaultEnabled: true,
    icon: '🏷️'
  },
  VENDOR_MANAGEMENT: {
    key: 'vendorManagement',
    name: 'Vendor Management',
    category: FEATURE_CATEGORIES.INVENTORY,
    description: 'Manage vendors and suppliers',
    defaultEnabled: true,
    icon: '🤝'
  },
  CUSTOMER_LEDGER: {
    key: 'customerLedger',
    name: 'Customer Ledger',
    category: FEATURE_CATEGORIES.INVENTORY,
    description: 'Track customer transactions and ledger accounts',
    defaultEnabled: true,
    icon: '📒'
  },

  // Users Category
  STAFF: {
    key: 'staff',
    name: 'Staff',
    category: FEATURE_CATEGORIES.USERS,
    description: 'Staff member management',
    defaultEnabled: true,
    icon: '👔'
  },

  // Dashboard Category
  DASHBOARD: {
    key: 'dashboard',
    name: 'Dashboard',
    category: FEATURE_CATEGORIES.DASHBOARD,
    description: 'School analytics and overview dashboard',
    defaultEnabled: true,
    icon: '📊',
    subFeatures: {
      quickActions: {
        key: 'quickActions',
        name: 'Quick Actions',
        defaultEnabled: true,
        items: ['students', 'teachers', 'routes', 'classes', 'alerts', 'settings']
      },
      statsCards: {
        key: 'statsCards',
        name: 'Statistics Cards',
        defaultEnabled: true,
        items: ['totalStudents', 'activeRoutes', 'attendance', 'performance', 'teachers', 'pendingTasks']
      },
      charts: {
        key: 'charts',
        name: 'Charts & Analytics',
        defaultEnabled: true,
        items: ['monthlyTrends', 'recentActivity', 'feeManagement', 'enrollmentTrends', 'vehicleRoutes']
      }
    }
  },

  // Settings Category
  ABOUT: {
    key: 'about',
    name: 'About School',
    category: FEATURE_CATEGORIES.SETTINGS,
    description: 'School information and profile',
    defaultEnabled: true,
    icon: 'ℹ️'
  },
  PERMISSIONS: {
    key: 'permissions',
    name: 'Permissions & Roles',
    category: FEATURE_CATEGORIES.SETTINGS,
    description: 'User roles and permissions management',
    defaultEnabled: true,
    icon: '🔐'
  },
  FAQ: {
    key: 'faq',
    name: 'FAQ Management',
    category: FEATURE_CATEGORIES.SETTINGS,
    description: 'Manage frequently asked questions',
    defaultEnabled: true,
    icon: '❔'
  }
}

// Helper function to get features by category
export const getFeaturesByCategory = (category) => {
  return Object.values(FEATURES).filter(feature => feature.category === category)
}

// Helper function to get all categories with their features
export const getCategorizedFeatures = () => {
  const categorized = {}
  
  Object.values(FEATURE_CATEGORIES).forEach(category => {
    categorized[category] = getFeaturesByCategory(category)
  })
  
  return categorized
}

// Helper function to get default feature states
export const getDefaultFeatureStates = () => {
  const defaults = {}
  
  Object.values(FEATURES).forEach(feature => {
    defaults[feature.key] = {
      enabled: feature.defaultEnabled,
      ...(feature.subFeatures && {
        subFeatures: Object.values(feature.subFeatures).reduce((acc, subFeature) => {
          acc[subFeature.key] = {
            enabled: subFeature.defaultEnabled,
            ...(subFeature.items && {
              items: subFeature.items.reduce((itemAcc, item) => {
                itemAcc[item] = true
                return itemAcc
              }, {})
            })
          }
          return acc
        }, {})
      })
    }
  })
  
  return defaults
}

// Category display names and icons
export const CATEGORY_LABELS = {
  [FEATURE_CATEGORIES.INVENTORY]: 'Inventory',
  [FEATURE_CATEGORIES.USERS]: 'Users',
  [FEATURE_CATEGORIES.DASHBOARD]: 'Dashboard',
  [FEATURE_CATEGORIES.SETTINGS]: 'Settings'
}

export const CATEGORY_ICONS = {
  [FEATURE_CATEGORIES.INVENTORY]: '📦',
  [FEATURE_CATEGORIES.USERS]: '👥',
  [FEATURE_CATEGORIES.DASHBOARD]: '📊',
  [FEATURE_CATEGORIES.SETTINGS]: '⚙️'
}
